import {
  subclass,
  property,
} from "@arcgis/core/core/accessorSupport/decorators";
import { messageBundle, tsx } from "@arcgis/core/widgets/support/widget";
import Widget from "@arcgis/core/widgets/Widget";
import { VNode } from "src/interfaces/interfaces";

import t9nAppMessages from "../../t9n/App/App.json";
import ContentAreaViewModel from "../ui/ContentArea/ContentAreaViewModel";
import "./DeleteModal/DeleteModal.scss";

export interface DeleteModalProperties extends __esri.WidgetProperties {
  appInfo: __esri.PortalItem;
  orgUrl: string;
  contentAreaVM: ContentAreaViewModel;
  portal: __esri.Portal;
}

@subclass("Landing.widgets.DeleteModal")
export default class DeleteModal extends Widget {
  //--------------------------------------------------------------------------
  //
  //  Properties
  //
  //--------------------------------------------------------------------------

  @property()
  appInfo: __esri.PortalItem;

  @property()
  orgUrl: string;

  @property()
  contentAreaVM: ContentAreaViewModel;

  @property()
  portal: __esri.Portal;

  @property()
  @messageBundle("dist/assets/t9n/App/App")
  appMessages: typeof t9nAppMessages;

  @property()
  modalVisible: boolean = false;

  @property()
  recycleEnabled: boolean;

  @property()
  _modalDOMContent: VNode[] = [];
  @property()
  _canDelete: boolean;
  @property()
  _isDeleting: boolean = false;
  @property()
  _isQueryingForCanDelete: boolean;

  //--------------------------------------------------------------------------
  //
  //  Lifecycle Methods
  //
  //--------------------------------------------------------------------------

  constructor(props: DeleteModalProperties) {
    super(props);
  }

  postInitialize(): void {
    this.recycleEnabled = this.portal?.sourceJSON?.recycleBinEnabled;
  }

  render() {
    const { close, deleteLabel } = this.appMessages.myApps.delete;
    return (
      <calcite-modal
        class="delete-modal"
        open={this.modalVisible}
        kind="danger"
        scale="m"
        width-scale="s"
        messageOverrides={{ close: close }}
        afterCreate={(modal: HTMLCalciteModalElement) => {
          modal.setFocus();
          modal.addEventListener("calciteModalClose", () => {
            this.hideModal();
          });
        }}
      >
        <h1 slot="header">{deleteLabel}</h1>

        <div slot="content">{this._renderContentArea()}</div>
        <div slot="secondary">{this._renderSecondaryBtns()}</div>
        <div slot="primary">{this._renderPrimaryBtns()}</div>
      </calcite-modal>
    );
  }

  //--------------------------------------------------------------------------
  //
  //  Public Methods
  //
  //--------------------------------------------------------------------------
  showModal(): void {
    this.modalVisible = true;
    this._isQueryingForCanDelete = true;
    this._queryForCanDelete().then(() => {
      setTimeout(() => {
        // very slight delay to ease the calcite-loader out
        this._isQueryingForCanDelete = false;
      }, 700);
    });
  }

  hideModal(): void {
    this.modalVisible = false;
  }

  //--------------------------------------------------------------------------
  //
  //  Private Methods
  //
  //--------------------------------------------------------------------------

  private _renderContentArea(): VNode {
    if (this._isQueryingForCanDelete) {
      return <calcite-loader></calcite-loader>;
    } else {
      return !this._canDelete
        ? this._renderDeleteProtection()
        : this._renderDeleteConfirmation();
    }
  }

  private _renderSecondaryBtns(): VNode {
    const { cancel } = this.appMessages.myApps.delete;
    if (this._isQueryingForCanDelete) {
      return (
        <calcite-button
          key="delete_cancelBtn_fake"
          scale="l"
          class="placeholder-button"
        >
          {" "}
          {cancel}
        </calcite-button>
      );
    } else {
      return (
        <calcite-button
          key="delete_cancelBtn"
          width="full"
          appearance="outline"
          kind="danger"
          alignment="center"
          scale="l"
          onclick={this.hideModal.bind(this)}
        >
          {cancel}
        </calcite-button>
      );
    }
  }

  private _renderPrimaryBtns(): VNode {
    const { deleteLabel, permanentlyDelete } = this.appMessages.myApps.delete;
    if (this._isQueryingForCanDelete) {
      return (
        <calcite-button
          key="delete_deleteBtn_fake"
          scale="l"
          class="placeholder-button"
        >
          {deleteLabel}
        </calcite-button>
      );
    } else {
      return (
        this._canDelete && (
          <calcite-split-button
            key="deleteSplitBtn"
            primary-text={deleteLabel}
            width="full"
            appearance="solid"
            kind="danger"
            alignment="center"
            scale="l"
            primary-icon-start="recycle-bin"
            loading={this._isDeleting}
            disabled={this._isDeleting}
            oncalciteSplitButtonPrimaryClick={this._performDelete.bind(
              this,
              false
            )}
          >
            <calcite-dropdown-group kind="danger" selection-mode="none">
              <calcite-dropdown-item
                kind="danger"
                icon-start="trash"
                oncalciteDropdownItemSelect={() => {
                  this._performDelete(true);
                }}
              >
                {permanentlyDelete}
              </calcite-dropdown-item>
            </calcite-dropdown-group>
          </calcite-split-button>
        )
      );
    }
  }

  private async _queryForCanDelete() {
    const deleteCheckRes = await this.contentAreaVM.publishAppVM.checkCanDelete(
      this.appInfo.id
    );
    this._canDelete = deleteCheckRes.success;
    return this._canDelete;
  }

  private _renderDeleteProtection() {
    const { deleteProtectionMsg } = this.appMessages.myApps.delete;
    const { id, title } = this?.appInfo;

    return (
      <div key="canDeleteFalse">
        <p> {deleteProtectionMsg} </p>
        <p>
          <calcite-link
            icon-start="launch"
            href={`${this.orgUrl}/home/item.html?id=${id}#settings`}
            target="_blank"
          >
            {title}
          </calcite-link>
        </p>
      </div>
    );
  }

  private _renderDeleteConfirmation() {
    const { deleteConfirmMsg, recycleConfirmMsg, recycleConfirmMsg2 } =
      this.appMessages.myApps.delete;
    return (
      <div key="canDeleteTrue">
        <p>{this.recycleEnabled ? recycleConfirmMsg : deleteConfirmMsg}</p>
        {this.recycleEnabled ? (
          <calcite-notice
            open
            icon="recycle-bin"
            key="recycle-info"
            kind="danger"
          >
            <div slot="message">{recycleConfirmMsg2}</div>
          </calcite-notice>
        ) : null}
      </div>
    );
  }

  private _performDelete(permanentDelete: boolean): void {
    const { deleteSuccess, deleteFailed } = this.appMessages.myApps.delete;
    const { id, title } = this.appInfo;

    this._isDeleting = true;
    this.contentAreaVM.publishAppVM
      .deleteItem(id, permanentDelete)
      .then((deleteRes) => {
        this._isDeleting = false;
        this.hideModal();
        if (deleteRes === true) {
          this.contentAreaVM.showAlert(
            deleteSuccess.title,
            `"${title}" ${deleteSuccess.msg}`,
            "success"
          );
          this.contentAreaVM.myAppsDataVM.removeFromMyAppsOnDelete(id);
        } else {
          this.contentAreaVM.showAlert(
            deleteFailed.title,
            `${deleteFailed.msg1}: ${title} ${deleteFailed.msg2}. ${
              deleteRes !== false && deleteRes
            }`,
            "error"
          );
        }
      });
  }
}
